import React, { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import gsap from "gsap";
import { CODE_LENGTH, photoPath } from "../config";

/* ==================================================================
 * LOCKBOX — Rue du Port de Gênes
 *
 * box  →  form (first name + email)  →  code  →  dials  →  photograph
 *
 * The player gets a photograph of the Citadel and NOTHING ELSE. No
 * caption, no arrow, no "go to the Citadel". Working out where that
 * photograph was taken IS the puzzle. Resist the urge to help.
 *
 * ------------------------------------------------------------------
 * DEMO WARNING
 * The code is generated in this file and shown on screen. Fine for
 * testing, unshippable in production: the photograph is already in the
 * client bundle, so anyone can open devtools and skip the gate.
 *
 * Real version:
 *   name + email      → POST /checkpoint/2/request-code
 *   backend generates the code, stores it against the game session with
 *                       a short expiry, emails it
 *   dials submit code → POST /checkpoint/2/verify
 *   backend verifies code + session + previous checkpoint + payment
 *   backend RETURNS the photo URL
 *
 * The photo must not exist client-side before verification.
 * ================================================================== */

const PHOTO = "citadel-photo";

/* Pixels of drag per digit. Lower = twitchier. */
const DRAG_STEP = 18;
/* Wheel delta per digit. */
const WHEEL_STEP = 40;
/* Movement under this many px counts as a tap, not a drag. */
const TAP_SLOP = 6;

/* ------------------------------------------------------------------ *
 * DIAL
 *
 * Two ways to turn it, because one of them has to survive whatever
 * browser a tourist is holding:
 *
 *   1. Drag the barrel up or down.
 *   2. Tap the chevron above or below it.
 *
 * Notes on the implementation, which is deliberately dull:
 *   - No setPointerCapture. It throws on some mobile browsers and takes
 *     the whole handler down with it.
 *   - move/up are bound to window, not the element, so the gesture
 *     survives the finger leaving a 35px-wide dial.
 *   - Listeners are attached natively in an effect with [interactive]
 *     as the only dep, and onTurn is held in a ref, so a re-render
 *     mid-drag cannot tear the listeners down.
 *   - Falls back to touch events where PointerEvent is missing.
 * ------------------------------------------------------------------ */

function Dial({ value, onTurn, interactive }) {
  const shellRef = useRef(null);
  const turnRef = useRef(onTurn);
  const drag = useRef({ active: false, startY: 0, steps: 0, moved: 0 });
  const suppressClick = useRef(false);
  const wheelAcc = useRef(0);
  const [offset, setOffset] = useState(0);

  // keep the callback fresh without re-binding listeners
  useEffect(() => {
    turnRef.current = onTurn;
  });

  useEffect(() => {
    const el = shellRef.current;
    if (!el || !interactive) return undefined;

    const usePointer = typeof window.PointerEvent === "function";
    const yOf = (e) =>
      e.touches && e.touches.length ? e.touches[0].clientY : e.clientY;

    const buzz = () => {
      if (navigator.vibrate) navigator.vibrate(6);
    };

    const move = (e) => {
      if (!drag.current.active) return;
      const dy = yOf(e) - drag.current.startY;
      drag.current.moved = Math.max(drag.current.moved, Math.abs(dy));

      const steps = Math.round(dy / DRAG_STEP);
      if (steps !== drag.current.steps) {
        turnRef.current(steps - drag.current.steps);
        drag.current.steps = steps;
        buzz();
      }
      // the barrel lags between detents
      setOffset(Math.max(-8, Math.min(8, dy - steps * DRAG_STEP)));
      if (e.cancelable) e.preventDefault();
    };

    const up = () => {
      if (!drag.current.active) return;
      suppressClick.current = drag.current.moved >= TAP_SLOP;
      drag.current.active = false;
      setOffset(0);
      detach();
    };

    const detach = () => {
      if (usePointer) {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
        window.removeEventListener("pointercancel", up);
      } else {
        window.removeEventListener("touchmove", move);
        window.removeEventListener("touchend", up);
        window.removeEventListener("touchcancel", up);
      }
    };

    const down = (e) => {
      // state first — nothing before it can throw and strand the drag
      drag.current = { active: true, startY: yOf(e), steps: 0, moved: 0 };
      suppressClick.current = false;

      if (usePointer) {
        window.addEventListener("pointermove", move, { passive: false });
        window.addEventListener("pointerup", up);
        window.addEventListener("pointercancel", up);
      } else {
        window.addEventListener("touchmove", move, { passive: false });
        window.addEventListener("touchend", up);
        window.addEventListener("touchcancel", up);
      }
    };

    const downEvent = usePointer ? "pointerdown" : "touchstart";
    el.addEventListener(downEvent, down, { passive: true });

    return () => {
      el.removeEventListener(downEvent, down);
      detach();
    };
  }, [interactive]);

  const step = (delta) => {
    if (suppressClick.current) {
      suppressClick.current = false;
      return;
    }
    onTurn(delta);
    if (navigator.vibrate) navigator.vibrate(6);
  };

  const onWheel = (e) => {
    if (!interactive) return;
    wheelAcc.current += e.deltaY;
    while (Math.abs(wheelAcc.current) >= WHEEL_STEP) {
      const dir = wheelAcc.current > 0 ? -1 : 1;
      onTurn(dir);
      wheelAcc.current += dir * WHEEL_STEP;
    }
  };

  return (
    <DialShell ref={shellRef} $interactive={interactive} onWheel={onWheel}>
      <Window>
        <Strip style={{ transform: `translateY(${offset}px)` }}>
          <Ghost>{(value + 1) % 10}</Ghost>
          <Current key={value}>{value}</Current>
          <Ghost>{(value + 9) % 10}</Ghost>
        </Strip>
      </Window>

      {interactive && (
        <>
          <Step $up type="button" aria-label="up" onClick={() => step(1)}>
            <Chevron $up />
          </Step>
          <Step type="button" aria-label="down" onClick={() => step(-1)}>
            <Chevron />
          </Step>
        </>
      )}
    </DialShell>
  );
}

/* ------------------------------------------------------------------ *
 * BOX
 * Module level, so a digit change never remounts it mid-gesture.
 * ------------------------------------------------------------------ */

function BoxView({ digits, onTurn, interactive, boxRef, lidRef }) {
  return (
    <BoxShell ref={boxRef}>
      <Lid ref={lidRef} />
      <Face>
        <Frame1 />
        <Frame2 />
        <Rosette $tl />
        <Rosette $tr />
        <Rosette $bl />
        <Rosette $br />
        <Dials>
          {digits.map((d, i) => (
            <Dial
              key={i}
              value={d}
              interactive={interactive}
              onTurn={(delta) => onTurn(i, delta)}
            />
          ))}
        </Dials>
      </Face>
      <Hasp />
      <Foot $left />
      <Foot />
    </BoxShell>
  );
}

/* ------------------------------------------------------------------ *
 * LOCKBOX
 * ------------------------------------------------------------------ */

export default function Lockbox({ copy, onFinish }) {
  /* stage: "box" | "form" | "code" | "dials" | "open" */
  const [stage, setStage] = useState("box");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [secret, setSecret] = useState(null);
  const [digits, setDigits] = useState(() => Array(CODE_LENGTH).fill(0));
  const [photoOk, setPhotoOk] = useState(false);

  const rootRef = useRef(null);
  const boxRef = useRef(null);
  const lidRef = useRef(null);
  const openedRef = useRef(false);

  useEffect(() => {
    const img = new Image();
    img.onload = () => setPhotoOk(true);
    img.onerror = () => setPhotoOk(false);
    img.src = photoPath(PHOTO);
  }, []);

  const submitForm = () => {
    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());
    if (!ok || !name.trim()) {
      setEmailError(!ok);
      return;
    }
    setEmailError(false);

    // DEMO — the backend does this, and never tells the client.
    const generated = Array.from({ length: CODE_LENGTH }, () =>
      Math.floor(Math.random() * 10)
    ).join("");
    setSecret(generated);
    setStage("code");
  };

  const turn = (index, delta) => {
    setDigits((d) => {
      const next = [...d];
      next[index] = (((next[index] + delta) % 10) + 10) % 10;
      return next;
    });
  };

  /* The box opens the moment the dials read the code. No submit button
     — the click of it giving way is the reward. */
  const entered = digits.join("");
  useEffect(() => {
    if (stage !== "dials" || openedRef.current || !secret) return undefined;
    if (entered !== secret) return undefined;

    openedRef.current = true;

    const ctx = gsap.context(() => {
      gsap
        .timeline({ onComplete: () => setStage("open") })
        .to(boxRef.current, { x: -3, duration: 0.06, repeat: 5, yoyo: true })
        .to(lidRef.current, {
          rotateX: -72,
          y: -8,
          duration: 0.9,
          ease: "power2.out",
        })
        .to(boxRef.current, { opacity: 0, duration: 0.5 }, "-=0.25");
    }, rootRef);

    return () => ctx.revert();
  }, [entered, secret, stage]);

  return (
    <Root ref={rootRef}>
      {stage === "box" && (
        <Panel>
          <BoxView
            digits={digits}
            onTurn={turn}
            interactive={false}
            boxRef={boxRef}
            lidRef={lidRef}
          />
          <Lead>{copy.boxFound}</Lead>
          <Sub>{copy.boxLocked}</Sub>
          <Primary onClick={() => setStage("form")}>{copy.boxWhoAsk}</Primary>
        </Panel>
      )}

      {stage === "form" && (
        <Panel>
          <Lead>{copy.formIntro}</Lead>
          <Form>
            <Field
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={copy.formName}
              autoComplete="given-name"
            />
            <Field
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={copy.formEmail}
              type="email"
              inputMode="email"
              autoComplete="email"
              $error={emailError}
            />
            {emailError && <FieldError>{copy.formInvalidEmail}</FieldError>}
            <Primary onClick={submitForm}>{copy.formSubmit}</Primary>
            <Privacy>{copy.formPrivacy}</Privacy>
          </Form>
        </Panel>
      )}

      {stage === "code" && (
        <Panel>
          <Lead>{copy.codeSentTitle}</Lead>
          <Sub>{copy.codeSentBody}</Sub>
          <DemoBlock>
            <DemoNote>{copy.codeDemoNote}</DemoNote>
            <DemoCode>{secret}</DemoCode>
          </DemoBlock>
          <Primary onClick={() => setStage("dials")}>{copy.codeGoToBox}</Primary>
        </Panel>
      )}

      {stage === "dials" && (
        <Panel>
          <BoxView
            digits={digits}
            onTurn={turn}
            interactive
            boxRef={boxRef}
            lidRef={lidRef}
          />
          <Hint>{copy.dialsHint}</Hint>
        </Panel>
      )}

      {stage === "open" && (
        <PhotoPanel onClick={onFinish}>
          <PhotoFrame>
            {photoOk ? (
              <Photo src={photoPath(PHOTO)} alt="" />
            ) : (
              <PhotoFallback viewBox="0 0 300 220" aria-hidden="true">
                <defs>
                  <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#b9a884" />
                    <stop offset="100%" stopColor="#7d6d51" />
                  </linearGradient>
                </defs>
                <rect width="300" height="220" fill="url(#sky)" />
                <path
                  d="M0 168 L40 156 L96 150 L150 146 L214 152 L262 160 L300 156 L300 220 L0 220 Z"
                  fill="#4d4433"
                />
                <path
                  d="M92 150 L92 108 L104 108 L104 100 L124 100 L124 108 L176 108 L176 98 L198 98 L198 108 L210 108 L210 152"
                  fill="#5c5240"
                />
                <path d="M126 108 L126 84 L152 78 L152 108 Z" fill="#665b46" />
                <path d="M0 200 L300 194 L300 220 L0 220 Z" fill="#3d3728" />
              </PhotoFallback>
            )}
          </PhotoFrame>
        </PhotoPanel>
      )}
    </Root>
  );
}

/* ------------------------------------------------------------------ */

const Root = styled.div`
  position: fixed;
  inset: 0;
  z-index: 30;
  background: radial-gradient(100% 70% at 50% 30%, #1e1810 0%, var(--ink) 74%),
    var(--ink);
  display: grid;
  place-items: center;
  padding: calc(env(safe-area-inset-top) + 3vh) 7vw
    calc(env(safe-area-inset-bottom) + 3vh);
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.3rem;
  width: 100%;
  max-width: 26rem;
  max-height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  text-align: center;
`;

/* --- the box --- */

const BoxShell = styled.div`
  position: relative;
  width: min(84vw, 340px);
  aspect-ratio: 8 / 5;
  perspective: 700px;
  filter: drop-shadow(0 26px 34px rgba(0, 0, 0, 0.75));
  flex: none;
`;

const brass = `linear-gradient(158deg, #c4a367 0%, #9d7f47 26%, #7d6334 58%, #a58850 82%, #6b5329 100%)`;

const Lid = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 24%;
  background: ${brass};
  border-radius: 5px 5px 2px 2px;
  transform-origin: top center;
  pointer-events: none;
  box-shadow: inset 0 -3px 6px rgba(48, 34, 12, 0.6),
    inset 0 2px 3px rgba(255, 236, 190, 0.28);

  &::after {
    content: "";
    position: absolute;
    inset: 14% 7% 22%;
    border: 1px solid rgba(58, 42, 16, 0.42);
    border-radius: 2px;
  }
`;

const Face = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 23%;
  bottom: 4%;
  background: ${brass};
  border-radius: 2px 2px 5px 5px;
  box-shadow: inset 0 3px 7px rgba(48, 34, 12, 0.55),
    inset 0 -2px 5px rgba(255, 236, 190, 0.14);
  padding: 6% 8%;
`;

/* Engraved frames as real elements, not ::before/::after. As
   pseudo-elements they painted above the dials and ate every tap. */
const Frame1 = styled.span`
  position: absolute;
  inset: 4% 3.5%;
  border: 1px solid rgba(56, 40, 14, 0.5);
  pointer-events: none;
`;

const Frame2 = styled.span`
  position: absolute;
  inset: 6.5% 5.5%;
  border: 1px solid rgba(255, 236, 190, 0.14);
  pointer-events: none;
`;

const Rosette = styled.span`
  position: absolute;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  pointer-events: none;
  background: radial-gradient(circle at 34% 30%, #d9bd83, #6b5329 70%);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);

  ${(p) => p.$tl && "top: 5.5%; left: 4.5%;"}
  ${(p) => p.$tr && "top: 5.5%; right: 4.5%;"}
  ${(p) => p.$bl && "bottom: 5.5%; left: 4.5%;"}
  ${(p) => p.$br && "bottom: 5.5%; right: 4.5%;"}
`;

const Dials = styled.div`
  position: relative;
  z-index: 2;
  height: 100%;
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 3%;
  align-items: stretch;
`;

const DialShell = styled.div`
  position: relative;
  border-radius: 3px;
  background: linear-gradient(180deg, #241a09, #4a3a1b 50%, #241a09);
  box-shadow: inset 0 0 5px rgba(0, 0, 0, 0.85);
  overflow: hidden;
  cursor: ${(p) => (p.$interactive ? "ns-resize" : "default")};
  touch-action: none; /* or the page steals the vertical drag */
  user-select: none;
  -webkit-user-select: none;
  -webkit-tap-highlight-color: transparent;
`;

const Window = styled.div`
  position: absolute;
  inset: 4% 8%;
  border-radius: 2px;
  background: linear-gradient(
    180deg,
    #6e6a5e 0%,
    #cfc8b4 30%,
    #ded8c6 50%,
    #cfc8b4 70%,
    #6e6a5e 100%
  );
  overflow: hidden;
  pointer-events: none;
`;

const Strip = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-family: var(--font-body);
  color: #2c2415;
`;

const Ghost = styled.span`
  font-size: 0.5rem;
  opacity: 0.3;
  line-height: 1.1;
`;

const roll = keyframes`
  from { transform: translateY(38%); opacity: 0.2; }
  to   { transform: translateY(0); opacity: 1; }
`;

const Current = styled.span`
  font-size: 1.1rem;
  font-weight: 500;
  line-height: 1.15;
  animation: ${roll} 0.18s ease-out;
`;

/* Visible, unmissable fallback. Sits above the window, which is
   pointer-events: none. */
const Step = styled.button`
  position: absolute;
  left: 0;
  right: 0;
  height: 30%;
  ${(p) => (p.$up ? "top: 0;" : "bottom: 0;")}
  display: grid;
  place-items: center;
  background: ${(p) =>
    p.$up
      ? "linear-gradient(to bottom, rgba(20,14,4,0.55), transparent)"
      : "linear-gradient(to top, rgba(20,14,4,0.55), transparent)"};
  padding: 0;
`;

const Chevron = styled.span`
  width: 7px;
  height: 7px;
  border-left: 1.5px solid rgba(232, 220, 196, 0.75);
  border-top: 1.5px solid rgba(232, 220, 196, 0.75);
  transform: rotate(${(p) => (p.$up ? "45deg" : "-135deg")});
`;

const Hasp = styled.div`
  position: absolute;
  right: -5%;
  top: 42%;
  width: 13%;
  height: 26%;
  border: 3px solid #8a6f3c;
  border-left: 0;
  border-radius: 0 5px 5px 0;
  pointer-events: none;
  box-shadow: 1px 1px 3px rgba(0, 0, 0, 0.5);
`;

const Foot = styled.span`
  position: absolute;
  bottom: -2%;
  ${(p) => (p.$left ? "left: 9%;" : "right: 9%;")}
  width: 7%;
  aspect-ratio: 1;
  border-radius: 50%;
  pointer-events: none;
  background: radial-gradient(circle at 36% 30%, #c1a165, #5c4623 72%);
`;

/* --- text --- */

const Lead = styled.p`
  margin: 0;
  font-family: var(--font-display);
  font-size: clamp(1.2rem, 5.2vw, 1.55rem);
  line-height: 1.44;
  color: var(--parchment);
  max-width: 28ch;
`;

const Sub = styled.p`
  margin: 0;
  font-family: var(--font-body);
  font-size: 0.9rem;
  line-height: 1.6;
  color: var(--parchment-2);
`;

const Hint = styled.div`
  font-family: var(--font-body);
  font-size: 0.62rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--parchment);
  opacity: 0.42;
  max-width: 26ch;
`;

/* --- form --- */

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  width: 100%;
`;

const Field = styled.input`
  width: 100%;
  padding: 0.95rem 1.1rem;
  background: rgba(232, 220, 196, 0.04);
  border: 1px solid
    ${(p) => (p.$error ? "rgba(168, 65, 44, 0.8)" : "rgba(232, 220, 196, 0.22)")};
  font-family: var(--font-body);
  font-size: 1rem; /* 16px minimum or iOS Safari zooms on focus */
  color: var(--parchment);
  transition: border-color 0.25s ease;

  &::placeholder {
    color: rgba(232, 220, 196, 0.4);
  }
  &:focus {
    outline: none;
    border-color: rgba(192, 139, 62, 0.7);
  }
`;

const FieldError = styled.div`
  font-family: var(--font-body);
  font-size: 0.75rem;
  color: #c96a52;
  text-align: left;
`;

const Privacy = styled.div`
  font-family: var(--font-body);
  font-size: 0.68rem;
  line-height: 1.55;
  color: var(--parchment);
  opacity: 0.38;
`;

const Primary = styled.button`
  padding: 1rem 2.4rem;
  border: 1px solid rgba(192, 139, 62, 0.6);
  font-size: 0.68rem;
  letter-spacing: 0.26em;
  text-transform: uppercase;
  color: var(--brass);
  transition: background 0.3s ease;

  &:hover {
    background: rgba(192, 139, 62, 0.12);
  }
`;

/* --- demo --- */

const DemoBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1.1rem 1.4rem;
  border: 1px dashed rgba(232, 220, 196, 0.18);
`;

const DemoNote = styled.div`
  font-family: ui-monospace, Menlo, monospace;
  font-size: 0.62rem;
  color: var(--parchment);
  opacity: 0.36;
`;

const DemoCode = styled.div`
  font-family: ui-monospace, Menlo, monospace;
  font-size: 1.5rem;
  letter-spacing: 0.34em;
  text-indent: 0.34em;
  color: var(--parchment);
  opacity: 0.85;
`;

/* --- the photograph --- */

const surface = keyframes`
  from { opacity: 0; transform: translateY(26px) scale(0.96); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const PhotoPanel = styled.div`
  display: grid;
  place-items: center;
  width: 100%;
  cursor: pointer;
`;

const PhotoFrame = styled.div`
  width: min(84vw, 340px);
  padding: 12px 12px 34px;
  background: linear-gradient(168deg, #efe6d2, #d9cdb2);
  box-shadow: 0 24px 44px rgba(0, 0, 0, 0.75);
  transform: rotate(-1.2deg);
  animation: ${surface} 1.1s ease-out both;
`;

const Photo = styled.img`
  display: block;
  width: 100%;
  filter: sepia(0.42) contrast(1.05) saturate(0.7);
`;

const PhotoFallback = styled.svg`
  display: block;
  width: 100%;
  height: auto;
  filter: sepia(0.5) contrast(1.05);
`;
